import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { IDashboardApiService } from './dashboard-api.interface';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { SuccessResponse } from '../shared/success-response';
import { FindNetRevenueOverTimeQueryDto } from './dtos/find-net-revenue-over-time.query.dto';
import { FindNetRevenueOverTimeResponseDto } from './dtos/find-net-revenue-over-time.response.dto';
import { FindTop10BestSellersQueryDto } from './dtos/find-top-10-best-sellers.query.dto';
import { FindTop10BestSellersResponseDto } from './dtos/find-top-10-best-sellers.response.dto';
import { FindSalesSummaryResponseDto } from './dtos/find-sales-summary-response.dto';
import { FindSalesSummaryQueryDto } from './dtos/find-sales-summary.query.dto';
import { FindStoreInsightsQueryDto } from './dtos/find-store-insights.query.dto';
import { FindStoreInsightsResponseDto } from './dtos/find-store-insights.response.dto';
import { FindDashboardAnalyticsQueryDto } from './dtos/find-dashboard-analytics.query.dto';
import { FindDashboardAnalyticsResponseDto } from './dtos/find-dashboard-analytics.response.dto';

@Injectable({
  providedIn: 'root',
})
export class DashboardApiService implements IDashboardApiService {
  private http = inject(HttpClient);
  private readonly API_URL = `${environment.api}/dashboard-analytics`; 

  async findDashboardAnalytics(dto: FindDashboardAnalyticsQueryDto): Promise<FindDashboardAnalyticsResponseDto> {
    const params = new HttpParams({ fromObject: dto as any });
    const { data } = await firstValueFrom(
      this.http.get<SuccessResponse<FindDashboardAnalyticsResponseDto>>(
        `${this.API_URL}`,
        { params }
      )
    );
    return data;
  }

  async findSalesSummary(dto: FindSalesSummaryQueryDto): Promise<FindSalesSummaryResponseDto> {
    const params = new HttpParams({ fromObject: dto as any });
    const { data } = await firstValueFrom(
      this.http.get<SuccessResponse<FindSalesSummaryResponseDto>>(
        `${this.API_URL}/sales-summary`,
        { params }
      )
    );
    return data;
  }

  async findNetRevenueOverTime(
    dto: FindNetRevenueOverTimeQueryDto
  ): Promise<FindNetRevenueOverTimeResponseDto> {
    const params = new HttpParams({ fromObject: dto as any });
    const { data } = await firstValueFrom(
      this.http.get<SuccessResponse<FindNetRevenueOverTimeResponseDto>>(
        `${this.API_URL}/net-revenue-over-time`,
        { params }
      )
    );
    return data;
  }

  async findTop10BestSellers(dto: FindTop10BestSellersQueryDto): Promise<FindTop10BestSellersResponseDto> {
    const params = new HttpParams({ fromObject: dto as any });
    const { data } = await firstValueFrom(
      this.http.get<SuccessResponse<FindTop10BestSellersResponseDto>>(
        `${this.API_URL}/best-sellers`,
        { params }
      )
    );
    return data;
  }

  async findStoreInsights(dto: FindStoreInsightsQueryDto): Promise<FindStoreInsightsResponseDto> {
    const params = new HttpParams({ fromObject: dto as any });
    const { data } = await firstValueFrom(
      this.http.get<SuccessResponse<FindStoreInsightsResponseDto>>(
        `${this.API_URL}/store-insights`,
        { params }
      )
    );
    return data;
  }
}
